/**
 * URL Utilities
 * Configuration-based URL handling for multi-locale WordPress/Next.js sites
 */

import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { SiteConfig, MetadataTemplate } from './types'

export type { SiteConfig, MetadataTemplate }

/**
 * Tailwind CSS class merger utility
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Generate canonical URL from path and config
 */
export function getCanonicalUrl(path: string = '', config: SiteConfig): string {
  const cleanBase = config.baseUrl.endsWith('/') ? config.baseUrl.slice(0, -1) : config.baseUrl

  // Handle root path specially
  if (path === '/' || path === '') {
    return path === '/' ? `${cleanBase}/` : cleanBase
  }

  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  return `${cleanBase}/${cleanPath}`
}

/**
 * Generate language alternate URLs for hreflang
 */
export function getLanguageAlternates(
  basePath: string = '',
  config: SiteConfig
) {
  const alternates: Record<string, string> = {}
  const isRootPath = basePath === '/'
  const isEmpty = basePath === ''
  const cleanPath = basePath.startsWith('/') ? basePath.slice(1) : basePath

  for (const [code, prefix] of Object.entries(config.locales)) {
    if (isRootPath) {
      // For root path, preserve the trailing slash
      const localePath = prefix ? `${prefix}/` : '/'
      alternates[code] = getCanonicalUrl(localePath, config)
    } else if (isEmpty && prefix) {
      // For empty path with locale prefix, don't add trailing slash
      alternates[code] = getCanonicalUrl(prefix, config)
    } else {
      // For other paths, don't add trailing slash
      const localePath = prefix && cleanPath ? `${prefix}/${cleanPath}` : (cleanPath || prefix)
      alternates[code] = getCanonicalUrl(localePath, config)
    }
  }

  // Add x-default (points to default locale)
  const defaultPrefix = config.locales[config.defaultLocale] || ''
  if (isRootPath) {
    alternates['x-default'] = getCanonicalUrl('/', config)
  } else {
    const defaultPath = defaultPrefix && cleanPath ? `${defaultPrefix}/${cleanPath}` : cleanPath
    alternates['x-default'] = getCanonicalUrl(defaultPath, config)
  }

  return alternates
}

/**
 * Generate complete page metadata using config and template
 */
export function generatePageMetadata(
  template: MetadataTemplate,
  path: string = '',
  config: SiteConfig
) {
  const canonicalUrl = getCanonicalUrl(path, config)
  const alternates = getLanguageAlternates(path, config)

  return {
    title: template.title,
    description: template.description,
    keywords: template.keywords,
    authors: template.author ? [{ name: template.author }] : undefined,
    metadataBase: new URL(config.baseUrl),
    alternates: {
      canonical: canonicalUrl,
      languages: alternates,
    },
    openGraph: {
      title: template.title,
      description: template.description,
      url: canonicalUrl,
      siteName: config.siteName,
      type: (template.type || 'website') as any,
      images: template.image ? [{ url: template.image }] : undefined,
      publishedTime: template.publishedTime,
      modifiedTime: template.modifiedTime,
      ...(template.section && { section: template.section }),
      ...(template.tags && { tags: template.tags }),
    },
    twitter: {
      card: 'summary_large_image',
      title: template.title,
      description: template.description,
      images: template.image ? [template.image] : undefined,
      site: config.metadata?.twitter?.site,
      creator: config.metadata?.twitter?.creator,
    },
  }
}

/**
 * Get article URL by slug and locale using config
 */
export function getArticleUrl(
  slug: string,
  locale: string,
  contentType: string,
  config: SiteConfig
): string {
  const pathMap = config.pathMappings?.[contentType]
  if (!pathMap) {
    throw new Error(`No path mapping found for content type: ${contentType}`)
  }

  const path = pathMap[locale] || pathMap[config.defaultLocale]
  return `/${path}/${slug}`
}

/**
 * Get locale prefix for URL construction
 */
export function getLocalePrefix(locale: string, config: SiteConfig): string {
  if (locale === config.defaultLocale) {
    return ''
  }
  const prefix = config.locales[locale]
  return prefix ? `/${prefix}` : ''
}

/**
 * Build full canonical URL for article
 */
export function getArticleCanonicalUrl(
  slug: string,
  locale: string,
  contentType: string,
  config: SiteConfig
): string {
  const articlePath = getArticleUrl(slug, locale, contentType, config)
  return getCanonicalUrl(articlePath, config)
}

/**
 * Parse locale from pathname
 */
export function parseLocaleFromPathname(
  pathname: string,
  config: SiteConfig
): string {
  const segments = pathname.split('/').filter(Boolean)
  const firstSegment = segments[0]

  // Check if first segment matches any locale prefix
  for (const [locale, prefix] of Object.entries(config.locales)) {
    if (prefix && firstSegment === prefix) {
      return locale
    }
  }

  return config.defaultLocale
}

/**
 * Get home page URL for locale
 */
export function getHomeUrl(locale: string, config: SiteConfig): string {
  if (locale === config.defaultLocale) {
    return '/'
  }
  const prefix = config.locales[locale]
  return prefix ? `/${prefix}` : '/'
}

/**
 * Build localized path
 */
export function buildLocalizedPath(
  path: string,
  locale: string,
  config: SiteConfig
): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  const prefix = getLocalePrefix(locale, config)
  return `${prefix}${cleanPath}`
}

/**
 * Normalize URL to match config base URL
 */
export function normalizeUrl(url: string, config: SiteConfig): string {
  try {
    const parsed = new URL(url)
    const expectedBase = new URL(config.baseUrl)
    parsed.protocol = expectedBase.protocol
    parsed.hostname = expectedBase.hostname
    parsed.port = expectedBase.port
    return parsed.toString()
  } catch {
    return url
  }
}
